import { api } from '@/lib/api/axios';
import { toApiError } from '@/lib/api/error';
import type {
  ChangePasswordApiResponse,
  ChangePasswordRequestBody,
  LoginApiResponse,
  LoginRequestBody,
  RefreshApiResponse,
} from '@/types/auth';

/**
 * Typed wrappers around the four `POST /api/auth/*` endpoints shipped in Plan 06-02.
 *
 * Every wrapper:
 *  - Throws an `ApiError` (via `toApiError`) on non-2xx so consumers can `instanceof`-switch.
 *  - Returns the typed response object on success — matches the shapes documented in the
 *    Plan 06-03 `<interfaces>` block.
 *
 * `logoutRequest` is intentionally best-effort: a network failure must NOT block the
 * client-side logout — the calling code (`AuthContext.logout`) tries it, swallows the
 * error, and proceeds to clear local state regardless.
 */

export async function loginRequest(body: LoginRequestBody): Promise<LoginApiResponse> {
  try {
    const { data } = await api.post<LoginApiResponse>('/api/auth/login', body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function refreshRequest(refreshToken: string): Promise<RefreshApiResponse> {
  try {
    const { data } = await api.post<RefreshApiResponse>('/api/auth/refresh', { refreshToken });
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function logoutRequest(refreshToken?: string | null): Promise<void> {
  try {
    const body = refreshToken === undefined || refreshToken === null ? {} : { refreshToken };
    await api.post('/api/auth/logout', body);
  } catch {
    // Best-effort. The user has already decided to log out — never block UI on this.
  }
}

export async function changePasswordRequest(
  body: ChangePasswordRequestBody,
): Promise<ChangePasswordApiResponse> {
  try {
    const { data } = await api.post<ChangePasswordApiResponse>('/api/auth/change-password', body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}
